import { railsAlert } from '@/lib/rails-alert';

// Every Rails error page is a response with no <turbo-frame> in it. Turbo's
// answer to that is to replace the frame's contents with the bare words
// "Content missing" and then throw TurboFrameMissingError, and nothing in the
// dashboard caught the throw - so an error on any frame-scoped request
// destroyed whatever the user had filled in and told them nothing about why.
//
// The event is cancelable, and cancelling it skips both the throw and the
// "Content missing" wipe, so we take over: keep the frame as it was, tell the
// user, and report a handled event whose fingerprint separates one frame's
// failures from another's.
//
// Two boundaries this cannot reach, because Turbo never gets as far as the
// frame: a response with an empty body or a non-HTML content type is dropped
// by loadResponse, and a turbo-stream content type is claimed by StreamObserver
// first. The latter is why :turbo_stream is a Devise navigational format - see
// config/initializers/devise.rb.

interface FrameMissingDetail {
  response?: Response;
}

const INJECTED_FLASH_ATTRIBUTE = 'data-turbo-frame-missing';
const INJECTED_FLASH_FRAME_ATTRIBUTE = 'data-turbo-frame-missing-frame';

// Turbo's own wording, matched so uncaught reports from older bundles land on
// the same fingerprint as the handled ones below.
const TURBO_FRAME_MISSING_MESSAGE = /The response \((\d{3})\) did not contain the expected <turbo-frame id="([^"]+)">/;

export const turboFrameMissingFingerprint = (frameId: string, status: number): string[] => [
  'turbo-frame-missing',
  frameId,
  String(status),
];

export const parseTurboFrameMissingMessage = (message: string): { frameId: string; status: number } | null => {
  const match = TURBO_FRAME_MISSING_MESSAGE.exec(message);
  if (!match) return null;

  const [, status, frameId] = match;
  if (!status || !frameId) return null;

  return { frameId, status: Number(status) };
};

// Where CustomFailure sends an unauthenticated request. Hardcoded rather than
// threaded through from Rails: one literal against one route, and getting it
// wrong only costs the vaguer of two messages below.
const LOGIN_PATH = '/login';

// Where an account's unmet 2FA requirement sends the user. It's a full page
// with the way to enrol, so a frame that lands there leaves for it.
const TWO_FACTOR_REQUIRED_PATH = '/users/two_factor_required';

const pathOf = (url: string): string | null => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }
};

export const messageForResponse = ({
  status,
  redirected,
  url,
}: Pick<Response, 'status' | 'redirected' | 'url'>): string => {
  // An unauthenticated frame request is redirected to the login page, so by the
  // time fetch has followed it the status is 200 - keying on status alone would
  // tell a signed-out user to keep retrying something that cannot work. Landing
  // on the login page says outright that they are signed out; a redirect
  // somewhere else is more likely a permission boundary, so stay vaguer there.
  if (redirected && pathOf(url) === LOGIN_PATH) {
    return "You've been signed out. Reload the page to sign in again.";
  }
  if (redirected || status === 401 || status === 403) {
    return 'You may no longer have access here. Reload the page to see the latest state.';
  }
  if (status === 404 || status === 410) return 'That is no longer available. Reload the page to see the latest state.';

  return "Something went wrong and your last action didn't complete. Please try again.";
};

// A signed-out request redirected to login, or a 401/403, is the expected
// response to a stale session or a permission boundary - not a frame that
// failed to render - so the user still gets a flash but nothing is reported.
export const isBenignAuthFrameMiss = ({
  status,
  redirected,
  url,
}: Pick<Response, 'status' | 'redirected' | 'url'>): boolean =>
  (redirected && pathOf(url) === LOGIN_PATH) || status === 401 || status === 403;

const buildAlert = (message: string, frameId: string): HTMLDivElement => {
  // No `is-hidden`: that class is the pre-transition state, and the only thing
  // that strips it is showHiddenRailsAlerts() on jQuery ready, which has long
  // since run by the time we inject.
  const alert = document.createElement('div');
  alert.className = 'c-alert alert-error alert-flash';
  // Injected mid-interaction, far from the focused submit button, so without a
  // live region a screen-reader user gets the same silent failure as before.
  alert.setAttribute('role', 'alert');
  alert.dataset.alertType = 'error';
  alert.dataset.railsRenderedFlash = 'true';
  alert.setAttribute(INJECTED_FLASH_ATTRIBUTE, 'true');
  alert.setAttribute(INJECTED_FLASH_FRAME_ATTRIBUTE, frameId);

  const text = document.createElement('div');
  text.className = 'c-alert__msg';
  text.textContent = message;

  alert.appendChild(text);

  // The dismiss handler lives in the same global as , so on a layout
  // without it the button would render and do nothing.
  if (railsAlert()) {
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'c-button c-alert__close-button';
    close.dataset.dismiss = 'alert';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML =
      '<svg class="c-button__icon ai ai-close-rounded" role="presentation"><use xlink:href="#ai-close-rounded"></use></svg>';
    alert.appendChild(close);
  }

  return alert;
};

const clearInjectedFlashes = (selector = `[${INJECTED_FLASH_ATTRIBUTE}]`): void => {
  document.querySelectorAll(selector).forEach((stale) => stale.remove());
};

// Only the frame whose failure produced the message: a lazy frame elsewhere on
// the page finishing its own load says nothing about this one.
const clearFlashesForFrame = (event: Event): void => {
  const frameId = (event.target as Element | null)?.id;
  if (!frameId) return;

  clearInjectedFlashes(`[${INJECTED_FLASH_FRAME_ATTRIBUTE}="${CSS.escape(frameId)}"]`);
};

// A stream aimed at the flashes region is the app reporting an outcome, which
// supersedes ours - most often the success flash from the retry that worked.
const clearFlashesOnFlashStream = (event: Event): void => {
  const target = (event as CustomEvent<{ newStream?: Element }>).detail?.newStream?.getAttribute('target');
  if (target === 'flashes') clearInjectedFlashes();
};

const showErrorFlash = (frame: Element, frameId: string, message: string): void => {
  // One frame failing repeatedly should replace its message, not stack copies.
  clearInjectedFlashes();

  const alert = buildAlert(message, frameId);

  // The React-only dashboard layout has no #flashes region, so fall back to the
  // frame itself, which also puts the message where the user is already looking.
  const container = document.getElementById('flashes');
  if (container) {
    container.appendChild(alert);
  } else {
    frame.prepend(alert);
  }

  railsAlert()?.bindEventsToAlertsFromRails();

  // The API key form is long enough that its submit button sits well below the
  // flashes region, so an unscrolled message would read as nothing happening.
  alert.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
};

const report = (frameId: string, status: number, url: string): void => {
  // Path only: frame URLs can carry an `api_key` query param, and the redaction
  // in the Sentry entrypoint covers event.request.url, not our own context.
  const path = pathOf(url) ?? 'unparseable';

  window.Sentry?.captureException(
    new Error(`The response (${status}) did not contain the expected <turbo-frame id="${frameId}">`),
    {
      level: 'warning',
      tags: { turbo_frame: frameId, response_status: String(status) },
      contexts: { turbo_frame: { id: frameId, status, path } },
      fingerprint: turboFrameMissingFingerprint(frameId, status),
    },
  );
};

export const handleFrameMissing = (event: Event): void => {
  const frame = event.target as HTMLElement | null;
  const { response } = ((event as CustomEvent<FrameMissingDetail>).detail ?? {}) as FrameMissingDetail;

  // Turbo redirects the event to documentElement when the frame has been
  // disconnected mid-request, e.g. a drawer closed before the response landed.
  // There is no frame left to annotate and nobody waiting on the answer, so
  // leave Turbo's default handling in place rather than prepending an alert
  // into <html>. Same for a detail we can't read: better an uncaught report
  // than a swallowed failure we know nothing about.
  if (!frame || frame.tagName !== 'TURBO-FRAME' || !response) return;

  event.preventDefault();

  if (response.redirected && pathOf(response.url) === TWO_FACTOR_REQUIRED_PATH) {
    window.location.assign(response.url);
    return;
  }

  const frameId = frame.id || 'unknown';

  // Report first: preventDefault has already suppressed Turbo's own error, so
  // anything thrown while rendering the flash would take the report with it.
  // The benign auth subset gets the flash but no report, so genuine
  // 404/410/5xx misses aren't buried under expected signed-out traffic.
  if (!isBenignAuthFrameMiss(response)) {
    report(frameId, response.status, response.url);
  }
  showErrorFlash(frame, frameId, messageForResponse(response));
};

export const registerTurboFrameMissingHandler = (): void => {
  document.addEventListener('turbo:frame-missing', handleFrameMissing);

  // Nothing clears #flashes on a frame navigation, so a retry that succeeds
  // would otherwise render its success message directly under our stale error.
  // Both fire only on a render that went through, never on the frame-missing
  // path, so neither can wipe the message we just injected.
  document.addEventListener('turbo:frame-load', clearFlashesForFrame);
  document.addEventListener('turbo:before-stream-render', clearFlashesOnFlashStream);
};
